import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api-client';
import axios from 'axios';
import toast from 'react-hot-toast';
import Logo from './Logo';
import Dropdown from './shared/Dropdown';
import { useTheme } from '../hooks/useTheme';

export default function AIConnectionSetupModal({ isOpen, onClose, onSuccess }) {
  const { currentThemeType, themeTypes } = useTheme();
  const [issues, setIssues] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [isLoadingHealth, setIsLoadingHealth] = useState(true);

  const isLightTheme = currentThemeType === themeTypes.LIGHT;

  // Tab state - 'provider' is default
  const [activeTab, setActiveTab] = useState('provider');

  // Connection testing states
  const [isTestingConnections, setIsTestingConnections] = useState(false);
  const [connectionTestResults, setConnectionTestResults] = useState(new Map());

  // Job expansion states
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const [expandedProviders, setExpandedProviders] = useState(new Set());

  // Per-job states (keyed by job_id)
  const [selectedConnections, setSelectedConnections] = useState(new Map());
  const [newApiKeys, setNewApiKeys] = useState(new Map());
  const [savingJobs, setSavingJobs] = useState(new Set());
  const [testingNewKeys, setTestingNewKeys] = useState(new Set());

  // Per-provider states
  const [providerApiKeys, setProviderApiKeys] = useState(new Map());
  const [testingProviderKeys, setTestingProviderKeys] = useState(new Set());
  const [savingProviders, setSavingProviders] = useState(new Set());
  const [providerKeyUrls, setProviderKeyUrls] = useState(new Map());

  const abortControllerRef = useRef(null);

  // Load health data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadHealthData();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen]);

  const loadHealthData = async () => {
    setIsLoadingHealth(true);
    try {
      const data = await api.get('/api/v1/admin/ai/model-configurations/health');
      setHealthData(data);
      setIssues(data.issues);

      // Parse provider key URLs if provided
      if (data.provider_key_urls) {
        const urlsMap = new Map();
        Object.entries(data.provider_key_urls).forEach(([provider, url]) => {
          urlsMap.set(provider, url);
        });
        setProviderKeyUrls(urlsMap);
      }

      // Start testing connections for all unique providers
      if (data.issues.length > 0) {
        await testAllConnections(data.issues);
      }
    } catch (error) {
      console.error('Failed to load health data:', error);
      toast.error('Failed to load AI configuration health');
    } finally {
      setIsLoadingHealth(false);
    }
  };

  const testAllConnections = async (issuesList) => {
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsTestingConnections(true);

    try {
      // Get unique providers from issues
      const providers = [...new Set(issuesList.map(issue => issue.provider))];

      // Fetch all connections
      const allConnections = await api.get('/api/v1/admin/ai/model-connections', { signal });
      if (signal.aborted) return;

      // Group connections by provider and test them
      const results = new Map();

      for (const provider of providers) {
        const providerConnections = allConnections.filter(
          conn => conn.provider === provider
        );

        if (providerConnections.length === 0) {
          results.set(provider, []);
          continue;
        }

        // Get model identifier for this provider from issues
        const issueForProvider = issuesList.find(issue => issue.provider === provider);
        const modelIdentifier = issueForProvider?.model_identifier;

        // Test each connection for this provider with the model identifier
        const testPromises = providerConnections.map(async (conn) => {
          try {
            const result = await api.post(
              `/api/v1/admin/ai/model-connections/${conn.id}/test`,
              { model_identifier: modelIdentifier },
              { signal }
            );
            return { connection: conn, success: result.success };
          } catch (error) {
            if (axios.isCancel(error) || signal.aborted) {
              throw error;
            }
            return { connection: conn, success: false };
          }
        });

        const testResults = await Promise.all(testPromises);
        if (signal.aborted) return;

        // Store working connections for this provider
        const workingConnections = testResults
          .filter(r => r.success)
          .map(r => r.connection);

        results.set(provider, workingConnections);

        // Auto-select first working connection for configurations with this provider
        if (workingConnections.length > 0) {
          const newSelections = new Map(selectedConnections);
          issuesList
            .filter(issue => issue.provider === provider)
            .forEach(issue => {
              if (!newSelections.has(issue.config_id)) {
                newSelections.set(issue.config_id, workingConnections[0].id);
              }
            });
          setSelectedConnections(newSelections);
        }
      }

      setConnectionTestResults(results);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Failed to test connections:', error);
      }
    } finally {
      setIsTestingConnections(false);
      abortControllerRef.current = null;
    }
  };

  const toggleJobExpansion = (jobId) => {
    const newExpanded = new Set(expandedJobs);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedJobs(newExpanded);
  };

  const toggleProviderExpansion = (provider) => {
    const newExpanded = new Set(expandedProviders);
    if (newExpanded.has(provider)) {
      newExpanded.delete(provider);
    } else {
      newExpanded.add(provider);
    }
    setExpandedProviders(newExpanded);
  };

  // Group issues by provider
  const getIssuesByProvider = () => {
    const grouped = new Map();
    issues.forEach(issue => {
      if (!grouped.has(issue.provider)) {
        grouped.set(issue.provider, []);
      }
      grouped.get(issue.provider).push(issue);
    });
    return grouped;
  };

  const handleTestProviderApiKey = async (provider) => {
    const apiKey = providerApiKeys.get(provider);
    if (!apiKey?.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    const newTesting = new Set(testingProviderKeys);
    newTesting.add(provider);
    setTestingProviderKeys(newTesting);

    try {
      // Get all configurations for this provider
      const providerIssues = issues.filter(issue => issue.provider === provider);

      if (providerIssues.length === 0) {
        toast.error('No configurations found for this provider');
        return;
      }

      // Step 1: Use the first config to test and create the connection
      const firstConfig = providerIssues[0];

      const createdConnection = await api.post(
        `/api/v1/admin/ai/model-configurations/${firstConfig.config_id}/create-connection`,
        { api_key: apiKey }
      );

      // Step 2: Get the connection ID from the response
      const connectionId = createdConnection.id;

      if (!connectionId) {
        toast.error('Connection created but no ID returned');
        return;
      }

      toast.success(`API key verified! Applying to configurations that need it...`);

      // Step 3: Assign this connection ONLY to configurations that have issues (no connection or invalid connection)
      // providerIssues already contains only configs from the issues list, so we're only updating broken ones
      const updatePromises = providerIssues.map(issue =>
        api.put(`/api/v1/admin/ai/model-configurations/${issue.config_id}`, {
          connection_id: connectionId
        })
      );

      await Promise.all(updatePromises);

      // Collapse the provider
      const newExpanded = new Set(expandedProviders);
      newExpanded.delete(provider);
      setExpandedProviders(newExpanded);

      // Clear the API key input for this provider
      const newKeys = new Map(providerApiKeys);
      newKeys.delete(provider);
      setProviderApiKeys(newKeys);

      // Reload health data to get the new connection and updated issues list
      await loadHealthData();

      toast.success(`All ${provider} configurations updated!`);

      // Check if all issues are resolved after reload
      const remainingIssues = issues.filter(issue => issue.provider !== provider);
      if (remainingIssues.length === 0) {
        // Small delay to show the success message before closing
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to test provider API key:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Invalid API key';
      toast.error(errorMsg);
    } finally {
      const newTesting = new Set(testingProviderKeys);
      newTesting.delete(provider);
      setTestingProviderKeys(newTesting);
    }
  };

  const handleSaveConnection = async (issue) => {
    const selectedConnection = selectedConnections.get(issue.config_id);
    if (!selectedConnection) {
      toast.error('Please select a connection');
      return;
    }

    const newSaving = new Set(savingJobs);
    newSaving.add(issue.config_id);
    setSavingJobs(newSaving);

    try {
      await api.put(`/api/v1/admin/ai/model-configurations/${issue.config_id}`, {
        connection_id: selectedConnection
      });

      toast.success(`Connection configured for ${issue.config_name}`);

      // Collapse the job
      const newExpanded = new Set(expandedJobs);
      newExpanded.delete(issue.config_id);
      setExpandedJobs(newExpanded);

      // Reload health data
      await loadHealthData();

      // Check if all issues are resolved
      const remainingIssues = issues.filter(i => i.config_id !== issue.config_id);
      if (remainingIssues.length === 0) {
        // Small delay to show the success message before closing
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to save connection:', error);
      toast.error('Failed to save connection');
    } finally {
      const newSaving = new Set(savingJobs);
      newSaving.delete(issue.config_id);
      setSavingJobs(newSaving);
    }
  };

  const handleTestNewApiKey = async (issue) => {
    const apiKey = newApiKeys.get(issue.config_id);
    if (!apiKey?.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    const newTesting = new Set(testingNewKeys);
    newTesting.add(issue.config_id);
    setTestingNewKeys(newTesting);

    try {
      // Step 1: Create the connection
      const createdConnection = await api.post(
        `/api/v1/admin/ai/model-configurations/${issue.config_id}/create-connection`,
        { api_key: apiKey }
      );

      // Step 2: Get the connection ID from the response
      const connectionId = createdConnection.id;

      if (!connectionId) {
        toast.error('Connection created but no ID returned');
        return;
      }

      // Step 3: Assign this connection to the configuration
      await api.put(`/api/v1/admin/ai/model-configurations/${issue.config_id}`, {
        connection_id: connectionId
      });

      toast.success(`API key verified and connection created for ${issue.config_name}!`);

      // Collapse the job
      const newExpanded = new Set(expandedJobs);
      newExpanded.delete(issue.config_id);
      setExpandedJobs(newExpanded);

      // Clear the API key input for this job
      const newKeys = new Map(newApiKeys);
      newKeys.delete(issue.config_id);
      setNewApiKeys(newKeys);

      // Reload health data
      await loadHealthData();

      // Check if all issues are resolved
      const remainingIssues = issues.filter(i => i.config_id !== issue.config_id);
      if (remainingIssues.length === 0) {
        // Small delay to show the success message before closing
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to test API key:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Invalid API key';
      toast.error(errorMsg);
    } finally {
      const newTesting = new Set(testingNewKeys);
      newTesting.delete(issue.config_id);
      setTestingNewKeys(newTesting);
    }
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // If all issues resolved, call onSuccess
    if (issues.length === 0 && healthData) {
      onSuccess?.();
    }

    onClose();
  };

  const getIssueIcon = (issueType) => {
    if (issueType === 'no_connection') {
      return (
        <svg className="w-4 h-4 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    } else {
      return (
        <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    }
  };

  const getWorkingConnectionsForIssue = (issue) => {
    return connectionTestResults.get(issue.provider) || [];
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 backdrop-blur-sm flex items-center justify-center z-50 ${isLightTheme ? 'bg-black/30' : 'bg-black/80'}`}
      onClick={handleClose}
    >
      <div
        className={`rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col ${isLightTheme ? 'bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-300' : 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-purple-500/20'}`}
        style={{ color: 'var(--color-text-on-accent)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-5 flex-shrink-0 ${isLightTheme ? 'border-b border-gray-200' : 'border-b border-gray-700/50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/favicon.svg"
                alt="itgetsit logo"
                className="h-10"
              />
              <h2 className={`text-xl font-semibold ${isLightTheme ? 'text-gray-800' : 'text-gray-100'}`}>AI Connection Setup</h2>
            </div>
            <button
              onClick={handleClose}
              className={`p-1.5 rounded-lg transition-colors ${isLightTheme ? 'hover:bg-gray-200 text-gray-600' : 'hover:bg-gray-700/50 text-gray-400'}`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        {!isLoadingHealth && issues.length > 0 && (
          <div className={`px-6 flex-shrink-0 ${isLightTheme ? 'border-b border-gray-200' : 'border-b border-gray-700/50'}`}>
            <div className="flex gap-1">
              <button
                onClick={() => setActiveTab('provider')}
                className={`px-4 py-3 font-medium text-sm transition-all duration-200 border-b-2 ${
                  activeTab === 'provider'
                    ? 'text-purple-400 border-purple-500'
                    : 'text-gray-400 border-transparent hover:text-gray-300'
                }`}
              >
                By Provider
              </button>
              <button
                onClick={() => setActiveTab('job')}
                className={`px-4 py-3 font-medium text-sm transition-all duration-200 border-b-2 ${
                  activeTab === 'job'
                    ? 'text-purple-400 border-purple-500'
                    : 'text-gray-400 border-transparent hover:text-gray-300'
                }`}
              >
                By Job
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isLoadingHealth ? (
            <div className="text-center py-12">
              <div className={`inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-r-transparent ${isLightTheme ? 'border-rose-500' : 'border-purple-500'}`}></div>
              <p className={`mt-3 text-sm ${isLightTheme ? 'text-gray-600' : 'text-gray-400'}`}>Checking AI configurations...</p>
            </div>
          ) : issues.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className={`text-lg font-semibold mb-2 ${isLightTheme ? 'text-gray-800' : 'text-gray-200'}`}>All Connections Healthy</h3>
              <p className={`text-sm ${isLightTheme ? 'text-gray-600' : 'text-gray-400'}`}>
                {healthData?.total_count || 0} configuration{healthData?.total_count !== 1 ? 's' : ''} checked, all working properly
              </p>
            </div>
          ) : (
            <>
              {/* Connection Testing Status */}
              {isTestingConnections && (
                <div className={`mb-5 rounded-xl p-4 ${isLightTheme ? 'bg-rose-50 border border-rose-200' : 'bg-purple-500/10 border border-purple-500/30'}`} style={{ color: 'var(--color-text-on-accent)' }}>
                                  <div className="flex items-center gap-3">
                    <div className={`h-6 w-6 animate-spin rounded-full border-3 border-solid border-r-transparent ${isLightTheme ? 'border-rose-500' : 'border-purple-500'}`}></div>
                    <div>
                      <p className={`text-sm font-medium ${isLightTheme ? 'text-gray-800' : 'text-gray-200'}`}>Testing Connections</p>
                      <p className={`text-xs ${isLightTheme ? 'text-gray-600' : 'text-gray-400'}`}>
                        Checking all connections for affected providers...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Issue Count */}
              <p className={`text-sm mb-4 ${isLightTheme ? 'text-gray-700' : 'text-gray-300'}`}>
                {activeTab === 'provider'
                  ? `${getIssuesByProvider().size} provider${getIssuesByProvider().size !== 1 ? 's' : ''} need${getIssuesByProvider().size === 1 ? 's' : ''} attention:`
                  : `${issues.length} AI configuration${issues.length !== 1 ? 's' : ''} need${issues.length === 1 ? 's' : ''} attention:`
                }
              </p>

              {/* By Provider View */}
              {activeTab === 'provider' && (
                <div className="space-y-2">
                  {Array.from(getIssuesByProvider().entries()).map(([provider, providerIssues]) => {
                    const isExpanded = expandedProviders.has(provider);
                    const isTestingKey = testingProviderKeys.has(provider);
                    const workingConnections = connectionTestResults.get(provider) || [];
                    const hasWorkingConnections = workingConnections.length > 0;

                    return (
                      <div
                        key={provider}
                        className="border border-gray-700 bg-gray-800/50 rounded-xl overflow-hidden"
                      >
                        {/* Provider Header */}
                        <div
                          onClick={() => toggleProviderExpansion(provider)}
                          className="flex items-center gap-3 p-4 cursor-pointer"
                        >
                          {/* Chevron */}
                          <div className="flex items-center gap-3 flex-1">
                            <svg
                              className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                                isExpanded ? 'rotate-90' : ''
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>

                            {/* Provider Name */}
                            <div className="flex-1 min-w-0">
                              <span className="text-sm font-medium text-gray-200">
                                {provider}
                              </span>
                              <span className="text-sm text-gray-400 mx-2">•</span>
                              <span className="text-sm text-gray-400">
                                {providerIssues.length} configuration{providerIssues.length !== 1 ? 's' : ''}
                              </span>
                            </div>

                            {/* Working Connections Badge */}
                            {!isTestingConnections && hasWorkingConnections && (
                              <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {workingConnections.length} working
                              </span>
                            )}
                          </div>

                          {/* Get API Key Link */}
                          {providerKeyUrls.has(provider) && (
                            <a
                              href={providerKeyUrls.get(provider)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 transition-colors flex-shrink-0"
                              title={`Get ${provider} API key`}
                            >
                              <span>Get one?</span>
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>

                        {/* Expanded Provider Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-2 border-t border-gray-700/50 space-y-3">
                            {/* Provider Configuration */}
                            <div className="space-y-2">
                              {/* Input for API Key */}
                              {!hasWorkingConnections && (
                                <>
                                  <label className="block text-xs font-medium text-gray-400">
                                    API Key
                                  </label>
                                  <input
                                    type="password"
                                    value={providerApiKeys.get(provider) || ''}
                                    onChange={(e) => {
                                      const newKeys = new Map(providerApiKeys);
                                      newKeys.set(provider, e.target.value);
                                      setProviderApiKeys(newKeys);
                                    }}
                                    placeholder={`Enter ${provider} API key (e.g., sk-...)`}
                                    className="w-full px-4 py-2 bg-gray-900/50 border-2 border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all text-sm"
                                    disabled={isTestingKey}
                                  />
                                </>
                              )}
                            </div>

                            {/* Status Message and Action Button */}
                            {!hasWorkingConnections && !isTestingConnections && (
                              <div className="flex items-center justify-between gap-3">
                                {/* Status Message */}
                                <p className={`text-xs flex items-center gap-1 ${isLightTheme ? 'text-amber-700' : 'text-yellow-400'}`}>
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  No working connections found. Create a new one with a valid API key.
                                </p>

                                {/* Action Button */}
                                <button
                                  onClick={() => handleTestProviderApiKey(provider)}
                                  disabled={isTestingKey || !providerApiKeys.get(provider)?.trim()}
                                  className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg hover:from-purple-700 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-purple-500/25 whitespace-nowrap flex-shrink-0"
                                  style={{ color: 'var(--color-text-on-accent)' }}
                                >
                                  {isTestingKey ? (
                                    <span className="flex items-center gap-2">
                                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      Testing & Applying...
                                    </span>
                                  ) : 'Test & Apply'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* By Job View (Original) */}
              {activeTab === 'job' && (
                <div className="space-y-2">
                {issues.map((issue) => {
                  const isExpanded = expandedJobs.has(issue.config_id);
                  const workingConnections = getWorkingConnectionsForIssue(issue);
                  const hasWorkingConnections = workingConnections.length > 0;
                  const isSaving = savingJobs.has(issue.config_id);
                  const isTestingKey = testingNewKeys.has(issue.config_id);

                  return (
                    <div
                      key={issue.config_id}
                      className="border border-gray-700 bg-gray-800/50 rounded-xl overflow-hidden"
                    >
                      {/* Collapsed Header - Always Visible */}
                      <div
                        onClick={() => toggleJobExpansion(issue.config_id)}
                        className="flex items-center gap-3 p-4 cursor-pointer"
                      >
                        {/* Chevron */}
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>

                        {/* Issue Icon */}
                        <div className="flex-shrink-0">
                          {getIssueIcon(issue.issue_type)}
                        </div>

                        {/* Configuration Name and Issue */}
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-medium text-gray-200">
                            {issue.config_name}
                          </span>
                          <span className="text-sm text-gray-400 mx-2">•</span>
                          <span className="text-sm text-gray-400">
                            {issue.error_message}
                          </span>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-2 border-t border-gray-700/50 space-y-3">
                          {/* Connection Configuration */}
                          <div className="flex items-center gap-3">
                            {/* Configuration Name and Provider */}
                            <div className="flex-shrink-0">
                              <p className="text-sm font-medium text-gray-200">
                                <span>{issue.config_name}</span> <span className="text-gray-400">• {issue.provider}</span>
                              </p>
                            </div>

                            {/* Dropdown or Input */}
                            <div className="flex-1 min-w-0">
                              {isTestingConnections ? (
                                <div className="h-10 bg-gray-900/50 border-2 border-gray-700 rounded-lg flex items-center px-4">
                                  <span className="text-sm text-gray-500">Testing connections...</span>
                                </div>
                              ) : hasWorkingConnections ? (
                                <Dropdown
                                  value={selectedConnections.get(issue.config_id) || ''}
                                  onChange={(e) => {
                                    const newSelections = new Map(selectedConnections);
                                    newSelections.set(issue.config_id, e.target.value);
                                    setSelectedConnections(newSelections);
                                  }}
                                  options={workingConnections.map((connection) => ({
                                    value: connection.id,
                                    label: `${connection.name}${connection.provider ? ` (${connection.provider})` : ''}`
                                  }))}
                                  disabled={isSaving}
                                  className="w-full"
                                />
                              ) : (
                                <input
                                  type="password"
                                  value={newApiKeys.get(issue.config_id) || ''}
                                  onChange={(e) => {
                                    const newKeys = new Map(newApiKeys);
                                    newKeys.set(issue.config_id, e.target.value);
                                    setNewApiKeys(newKeys);
                                  }}
                                  placeholder="Enter API key (e.g., sk-...)"
                                  className="w-full px-4 py-2 bg-gray-900/50 border-2 border-gray-700 rounded-lg text-gray-100 placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all text-sm"
                                  disabled={isTestingKey}
                                />
                              )}
                            </div>
                          </div>

                          {/* Connection Status Message and Action Button Row */}
                          {!isTestingConnections && (
                            <div className="flex items-center justify-between gap-3">
                              {/* Status Message */}
                              {hasWorkingConnections ? (
                                <p className={`text-xs flex items-center gap-1 ${isLightTheme ? 'text-green-700' : 'text-green-400'}`}>
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {workingConnections.length} working connection{workingConnections.length !== 1 ? 's' : ''} available
                                </p>
                              ) : (
                                <p className={`text-xs flex items-center gap-1 ${isLightTheme ? 'text-amber-700' : 'text-yellow-400'}`}>
                                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  No working connections found. Create a new one with a valid API key.
                                </p>
                              )}

                              {/* Action Button */}
                              <button
                                onClick={() => hasWorkingConnections ? handleSaveConnection(issue) : handleTestNewApiKey(issue)}
                                disabled={
                                  isSaving ||
                                  isTestingKey ||
                                  (hasWorkingConnections ? !selectedConnections.get(issue.config_id) : !newApiKeys.get(issue.config_id)?.trim())
                                }
                                className="px-4 py-2 text-sm font-medium bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg hover:from-purple-700 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg shadow-purple-500/25 whitespace-nowrap flex-shrink-0"
                                style={{ color: 'var(--color-text-on-accent)' }}
                              >
                                {isSaving || isTestingKey ? (
                                  <span className="flex items-center gap-2">
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {hasWorkingConnections ? 'Save & Test' : 'Test & Save'}
                                  </span>
                                ) : hasWorkingConnections ? 'Save & Test' : 'Test & Save'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
